import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import ActBtn from 'components/buttons/ActBtn'
import Btn from 'components/buttons/Btn'
import ConfirmModal from 'components/ConfirmModal'
import Screen from 'components/layout/Screen'
import { omit } from 'lodash-es'
import printPDF from 'print-js'
import PropTypes from 'prop-types'
import { useContext, useEffect } from 'react'
import { Container, Dropdown, DropdownButton, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import API from 'state/API'
import { useUser } from 'state/Auth'
import { PRIVILEGES } from 'state/Constants'
import { fetchOrder, toggleCancelOrder, toggleCompleteOrder } from 'state/Orders'
import { useEnabledPrinters } from 'state/Printers'
import { useSettings } from 'state/Settings'
import { platform, StarPrinters, useBoolState, useModalState } from 'utils'
import OrderContext from './_context'
import Header from './Header'
import ItemsTable from './ItemsTable'
import NewAdjustmentModal from './Modals/NewAdjustmentModal'
import RescheduleModal from './Modals/RescheduleModal'
import ResendModal from './Modals/ResendModal'

const DropdownItemBtn = props => props.show === false? null : <Dropdown.Item as={Btn} swap {...omit(props, 'show')}/>

/**
 * @param title {string}
 * @param pageTitle {string}
 */
export default function CompleteOrder({ title, pageTitle }) {
	const { t }    = useTranslation()
	const dispatch = useDispatch()

	/** @type Order */
	const order               = useContext(OrderContext)
	const cancelled           = useBoolState(order.is_cancelled)
	const completed           = useBoolState(order.is_completed)
	const { enableCompleted } = useSettings()
	const user                = useUser()
	const hasPrinters         = useEnabledPrinters().length > 0
	const resendModal         = useModalState()
	const cancelModal         = useModalState()
	const rescheduleModal     = useModalState()
	const adjustModal         = useModalState()

	const cantReschedule = cancelled.is || completed.is || order.is_due
	const cantAdjust     = !order.canBeAdjusted() //complete orders can be adjusted just fine, and cancelled orders can be un-cancelled, so it would be easy to flip the switch, adjust, then cancel again; thus, no need to block adjustments in either of those scenarios
	const allowedAdjust  = user.useCanAdjustOrders()
	const allowedCancel  = user.usePrivilege(PRIVILEGES().CANCEL_ORDER)
	const allowTips      = order.useRestaurant().allow_tips

	useEffect(() => {
		cancelled.toggle(order.is_cancelled)
		completed.toggle(order.is_completed)
	}, [order.modified_at.getTime(), order.is_cancelled, order.is_completed]) // eslint-disable-line react-hooks/exhaustive-deps

	//can't be inlined because buggy i18next-scan can't find those t() calls inside another call's params......... and yep, cancellation is an exception spelling: it's the most used, worldwide
	const confirmCancel = cancelled.is? t('Do you want to undo the cancellation of') : t('Do you want to cancel')

	//region action methods
	async function doReload() {
		await dispatch(fetchOrder(order.id))
	}

	async function doPrint() {
		printPDF({
			printable: (await API.get(`order/${order.id}/sheet?base64`)).data,
			base64   : true
		})
	}

	function doReceipt() { // noinspection JSIgnoredPromiseFromCall
		StarPrinters.print(order)
	}

	async function optimisticFlip(bool, action) {
		bool.flip() //requests a flip on the boolean...
		return dispatch(action(order.id, !bool.is)) //but it hasn't flipped yet, so we still request the opposite
			.catch(() => bool.toggle(bool.is)) //optimistic call, so we toggle back in case of failures
	}

	function doCancel() {
		return optimisticFlip(cancelled, toggleCancelOrder) //promise ignored, so the modal's change isn't noticeable lol
	}

	async function doToggleComplete() {
		return optimisticFlip(completed, toggleCompleteOrder)
	}

	//endregion
	const showEllipsis = platform.isAndroid()
	const ActionsDropdown = (
		<DropdownButton id="actions" variant={showEllipsis? 'outline-dark' : 'outline-secondary'}
		                title={showEllipsis? <Icon icon="ellipsis-v"/> : t('Actions')} className="d-print-none">

			{/* Don't try to use ActBtn here! https://github.com/react-bootstrap/react-bootstrap/issues/5082 */}
			{!platform.isNative() && <DropdownItemBtn onClick={doPrint} icon="print">{t('Print full size')}</DropdownItemBtn>}
			{/* on Desktop the "Settings" link is not clickable because the tooltip is hover-based, but making it click-based will cause it to float in the void if you just get out of the menu. However, the hover action works fine in mobile (which is caused by a click), so we'll leave it this way. Maybe we just drop the link in case of !platform.mobile(), but idk */}
			{StarPrinters.shouldBeAvailable() &&
				<DropdownItemBtn icon="receipt" children={t('Print receipt')}
				                 onClick={doReceipt}
				                 disabled={!hasPrinters && <StarPrinters.NoPrinterWarning/>}
				/>
			}

			<DropdownItemBtn onClick={resendModal.doOpen} icon="paper-plane">{t('Resend')}</DropdownItemBtn>

			<Dropdown.Divider/>

			<DropdownItemBtn icon={['far', 'calendar-edit']} children={t('Reschedule')}
			                 onClick={rescheduleModal.doOpen}
			                 disabled={cantReschedule && <>{t("This order can't be rescheduled: it's already completed, canceled or due.")}{order.is_due? ' '+t('If you need to delay it, get in touch with the customer directly.') : ''}</>}
			/>

			<DropdownItemBtn icon={['far', 'money-check-edit-alt']} children={allowTips? t('Adjust payment/tip') : t('Adjust payment')}
			                 onClick={adjustModal.doOpen}
			                 show={allowedAdjust}
			                 disabled={cantAdjust && t("This order can't be adjusted: it's already completed, canceled, or way past due.")}
			/>

			<DropdownItemBtn icon="refresh" children={t('Reload')} onClick={doReload}/>

			{allowedCancel && <Dropdown.Divider/>}

			<DropdownItemBtn onClick={cancelModal.doOpen}
			                 variant={cancelled.is? 'info' : 'danger'}
			                 icon={cancelled.is? 'recycle' : ['fas', 'window-close']}
			                 children={cancelled.is? t('Undo cancel', { context: '...an order' }) : t('Cancel order')}
			                 show={allowedCancel}
			                 disabled={completed.is && t('This order is completed. To cancel it, first mark it as open again.')}
			/>
		</DropdownButton>
	)

	//TODO maybe we should identify which page it will be going to and, if it's another order, go to the correct list instead - this way, if the user is in e.g. Menu Items and opens an order notification, they can correctly get back to the Menu Items
	return <Screen back={order.statusSlug} corner={ActionsDropdown} {...{title, pageTitle}}>
		<Screen.Content>
			<Container>
				<Header {...{ isCancelled: cancelled.is, isCompleted: completed.is }}/>
				<Row><ItemsTable order={order}/></Row>
			</Container>
		</Screen.Content>

		{enableCompleted && <footer className="d-print-none">
			<ActBtn action={doToggleComplete} block size="lg"
			         variant={completed.is? 'warning' : 'success'}
			         swap icon={completed.is? 'undo-alt' : 'check'}
	             texts={[completed.is? t('Mark as Open') : t('Mark Completed'), t('Marking', { context: 'as completed' })]}
	             disabled={cancelled.is && t('This order is canceled. To mark as completed, first undo its cancellation in the top-right menu.')}
			/>
		</footer>}

		<ResendModal {...resendModal}/>

		<RescheduleModal order={order} {...rescheduleModal}/>

		{allowedCancel && <ConfirmModal {...cancelModal} doSubmit={doCancel} id="cancel-order"
		              dangerous={cancelled.isnt}
		              cancelGoBack freezeBody
		              confirmTexts={cancelled.is?
			              [t('Undo cancel', { context: '...an order' }), '', ''] :
										[t('Cancel order'), '', ''] //no need of extra texts because it's optimistic
									}>
			{t('{{confirmCancel}} order #{{num}}, for {{customer}}?', {
				confirmCancel,
				num     : order.number,
				customer: order.customer_name,
			})}
		</ConfirmModal>}

		{!cantAdjust && allowedAdjust && <NewAdjustmentModal {...adjustModal}/>}
	</Screen>
}
CompleteOrder.propTypes = {
	title    : PropTypes.node.isRequired,
	pageTitle: PropTypes.node.isRequired,
}
